import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useAuth } from "../auth/AuthContext";
import { db } from "../configs/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";

type ThemeName = "light" | "dark";

export interface AppTheme {
  name: ThemeName;

  radii: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    pill: string;
  };

  brand: {
    primary: string;
    primaryHover: string;
    primarySoft: string;
    electricBlue: string;
    deepSlate: string;
    deepInk: string;
    slate2: string;
    softLight: string;
    violet: string;
    indigo: string;
    amber: string;
    rose: string;
    emerald: string;
  };

  teal: {
    50?: string;
    100?: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800?: string;
    900?: string;
  };

  accents: {
    blue: string;
    violet: string;
    amber: string;
    rose: string;
  };

  buttons: {
    primary: any;
    secondary: any;
    tealPrimary: any;
    outlineTeal: any;
    ghost: any;
    danger: any;
    warning: any;
    info: any;
  };

  primary: string;
  primaryHover: string;
  primarySoft: string;
  accent: string;
  secondary: string;

  success: string;
  warning: string;
  danger: string;
  info: string;

  background: string;
  surface: string;
  surfaceAlt: string;
  elevated: string;
  overlay: string;

  text: string;
  textMuted: string;
  textSubtle: string;
  textOnPrimary: string;

  border: string;
  borderStrong: string;
  divider: string;

  inputBg: string;
  inputBorder: string;
  inputBorderFocus: string;
  inputPlaceholder: string;

  hover: string;
  pressed: string;
  selected: string;

  shadowSm: string;
  shadowMd: string;
  shadowLg: string;

  gradients: {
    pageBg: string;
    shellBg: string;

    primaryButton: string;
    primaryButtonHover: string;
    secondaryButton: string;
    secondaryButtonHover: string;

    tabHikesSelected: string;
    tabDeliverySelected: string;
    tabShopSelected: string;
  };

  muted: string;
}

interface CustomThemeContextValue {
  theme: AppTheme;
  themeName: ThemeName;
  isThemeReady: boolean;
  toggleTheme: () => Promise<void>;
  setThemeByName: (name: ThemeName) => Promise<void>;
}

const STORAGE_KEY = "app_theme_preference_v2";

/**
 * Best modern professional teal scale:
 * cleaner progression, better contrast, more premium than the original muddy teal 400.
 */
const tealScale = {
  50: "#F0FDFA",
  100: "#CCFBF1",
  200: "#99F6E4",
  300: "#5EEAD4",
  400: "#2DD4BF",
  500: "#14B8A6",
  600: "#0D9488",
  700: "#0F766E",
  800: "#115E59",
  900: "#134E4A",
};

const accentPalette = {
  blue: "#3B82F6",
  violet: "#8B5CF6",
  amber: "#F59E0B",
  rose: "#F43F5E",
};

const brandPalette = {
  primary: tealScale[500],
  primaryHover: tealScale[600],
  primarySoft: "rgba(20, 184, 166, 0.14)",

  electricBlue: "#14B8A6",
  deepSlate: "#1E293B",
  deepInk: "#0F172A",
  slate2: "#334155",
  softLight: "#F8FAFC",

  violet: "#8B5CF6",
  indigo: "#6366F1",
  amber: "#F59E0B",
  rose: "#F43F5E",
  emerald: "#22C55E",
};

const radiiTokens = {
  xs: "4px",
  sm: "5px",
  md: "12px",
  lg: "16px",
  xl: "20px",
  pill: "999px",
};

const common = {
  primary: tealScale[500],
  primaryHover: tealScale[600],
  primarySoft: "rgba(20, 184, 166, 0.14)",

  accent: brandPalette.violet,
  secondary: brandPalette.indigo,

  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  info: "#3B82F6",
};

const makeButtons = (
  mode: ThemeName,
  theme: Pick<
    AppTheme,
    | "text"
    | "textMuted"
    | "border"
    | "borderStrong"
    | "shadowSm"
    | "shadowMd"
    | "primaryHover"
  >
) => {
  const isDark = mode === "dark";

  const neutralBg = isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.92)";
  const neutralHover = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.04)";

  return {
    primary: {
      color: "#FFFFFF",
      bgImage: `linear-gradient(135deg, ${tealScale[500]} 0%, ${tealScale[400]} 100%)`,
      boxShadow: theme.shadowSm,
      _hover: {
        bgImage: `linear-gradient(135deg, ${theme.primaryHover} 0%, ${tealScale[500]} 100%)`,
        transform: "translateY(-1px)",
        boxShadow: theme.shadowMd,
      },
      _active: { transform: "translateY(0)" },
      borderRadius: radiiTokens.md,
      transition: "all 0.2s ease",
    },

    secondary: {
      color: theme.text,
      bg: neutralBg,
      border: `1px solid ${theme.border}`,
      _hover: {
        bg: neutralHover,
        borderColor: theme.borderStrong,
      },
      borderRadius: radiiTokens.md,
      transition: "all 0.2s ease",
    },

    tealPrimary: {
      color: "#FFFFFF",
      bgGradient: `linear-gradient(135deg, ${tealScale[500]}, ${tealScale[400]})`,
      boxShadow: theme.shadowSm,
      _hover: {
        bgGradient: `linear-gradient(135deg, ${tealScale[700]}, ${tealScale[500]})`,
        transform: "translateY(-1px)",
        boxShadow: theme.shadowMd,
      },
      _active: { transform: "translateY(0)" },
      borderRadius: radiiTokens.md,
      transition: "all 0.2s ease",
    },

    outlineTeal: {
      bg: "transparent",
      color: isDark ? tealScale[300] : tealScale[700],
      border: `1px solid ${isDark ? "rgba(94,234,212,0.26)" : tealScale[300]}`,
      _hover: {
        bg: isDark ? "rgba(45,212,191,0.10)" : "rgba(45,212,191,0.08)",
        borderColor: tealScale[400],
      },
      borderRadius: radiiTokens.md,
      transition: "all 0.2s ease",
    },

    ghost: {
      variant: "ghost",
      color: theme.textMuted,
      _hover: {
        bg: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
        color: theme.text,
      },
      borderRadius: radiiTokens.md,
    },

    danger: {
      color: "#FFFFFF",
      bgGradient: `linear-gradient(135deg, ${accentPalette.rose}, #FB7185)`,
      _hover: { filter: "brightness(0.98)", transform: "translateY(-1px)" },
      _active: { transform: "translateY(0)" },
      borderRadius: radiiTokens.md,
      transition: "all 0.2s ease",
    },

    warning: {
      color: "#111827",
      bgGradient: `linear-gradient(135deg, #FCD34D, ${accentPalette.amber})`,
      _hover: { filter: "brightness(0.99)", transform: "translateY(-1px)" },
      _active: { transform: "translateY(0)" },
      borderRadius: radiiTokens.md,
      transition: "all 0.2s ease",
    },

    info: {
      color: "#FFFFFF",
      bgGradient: `linear-gradient(135deg, ${accentPalette.blue}, #60A5FA)`,
      _hover: { filter: "brightness(1.03)", transform: "translateY(-1px)" },
      _active: { transform: "translateY(0)" },
      borderRadius: radiiTokens.md,
      transition: "all 0.2s ease",
    },
  };
};

const lightThemeBase: Omit<AppTheme, "buttons" | "teal" | "accents"> = {
  name: "light",
  radii: radiiTokens,
  brand: brandPalette,

  ...common,

  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceAlt: "#F1F5F9",
  elevated: "#FFFFFF",
  overlay: "rgba(15, 23, 42, 0.42)",

  text: "#0F172A",
  textMuted: "#475569",
  textSubtle: "#64748B",
  textOnPrimary: "#FFFFFF",

  border: "#E2E8F0",
  borderStrong: "#CBD5E1",
  divider: "rgba(15, 23, 42, 0.08)",

  inputBg: "#FFFFFF",
  inputBorder: "#CBD5E1",
  inputBorderFocus: tealScale[500],
  inputPlaceholder: "#94A3B8",

  hover: "rgba(15, 23, 42, 0.04)",
  pressed: "rgba(15, 23, 42, 0.08)",
  selected: "rgba(20, 184, 166, 0.10)",

  shadowSm: "0 8px 20px rgba(15, 23, 42, 0.06)",
  shadowMd: "0 16px 34px rgba(15, 23, 42, 0.10)",
  shadowLg: "0 24px 48px rgba(15, 23, 42, 0.14)",

  gradients: {
    pageBg: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
    shellBg:
      "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.92) 100%)",

    primaryButton: `linear-gradient(135deg, ${tealScale[500]} 0%, ${tealScale[400]} 100%)`,
    primaryButtonHover: `linear-gradient(135deg, ${tealScale[600]} 0%, ${tealScale[500]} 100%)`,

    secondaryButton: `linear-gradient(135deg, ${brandPalette.indigo} 0%, ${tealScale[500]} 100%)`,
    secondaryButtonHover: "linear-gradient(135deg, #5558E8 0%, #0D9488 100%)",

    tabHikesSelected:
      "linear-gradient(135deg, rgba(20,184,166,0.14) 0%, rgba(45,212,191,0.08) 70%, rgba(255,255,255,0.98) 100%)",
    tabDeliverySelected:
      "linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(20,184,166,0.08) 70%, rgba(255,255,255,0.98) 100%)",
    tabShopSelected:
      "linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(20,184,166,0.08) 70%, rgba(255,255,255,0.98) 100%)",
  },

  muted: "#475569",
};

const darkThemeBase: Omit<AppTheme, "buttons" | "teal" | "accents"> = {
  name: "dark",
  radii: radiiTokens,
  brand: brandPalette,

  ...common,

  background: "#0F172A",
  surface: "#111827",
  surfaceAlt: "#1E293B",
  elevated: "#1A2436",
  overlay: "rgba(2, 6, 23, 0.72)",

  text: "#F8FAFC",
  textMuted: "#CBD5E1",
  textSubtle: "#94A3B8",
  textOnPrimary: "#FFFFFF",

  border: "rgba(255,255,255,0.08)",
  borderStrong: "rgba(255,255,255,0.14)",
  divider: "rgba(255,255,255,0.08)",

  inputBg: "#0F172A",
  inputBorder: "rgba(255,255,255,0.10)",
  inputBorderFocus: tealScale[400],
  inputPlaceholder: "#94A3B8",

  hover: "rgba(255,255,255,0.05)",
  pressed: "rgba(255,255,255,0.08)",
  selected: "rgba(45,212,191,0.14)",

  shadowSm: "0 10px 22px rgba(0,0,0,0.28)",
  shadowMd: "0 18px 40px rgba(0,0,0,0.36)",
  shadowLg: "0 26px 56px rgba(0,0,0,0.42)",

  gradients: {
    pageBg: "linear-gradient(180deg, #0F172A 0%, #111827 52%, #1E293B 100%)",
    shellBg:
      "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.025) 100%)",

    primaryButton: `linear-gradient(135deg, ${tealScale[500]} 0%, ${tealScale[400]} 100%)`,
    primaryButtonHover: `linear-gradient(135deg, ${tealScale[400]} 0%, ${tealScale[300]} 100%)`,

    secondaryButton: `linear-gradient(135deg, ${brandPalette.indigo} 0%, ${tealScale[500]} 100%)`,
    secondaryButtonHover: "linear-gradient(135deg, #7C83FF 0%, #14B8A6 100%)",

    tabHikesSelected:
      "linear-gradient(135deg, rgba(20,184,166,0.18) 0%, rgba(45,212,191,0.10) 55%, rgba(255,255,255,0.03) 100%)",
    tabDeliverySelected:
      "linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(20,184,166,0.10) 55%, rgba(255,255,255,0.03) 100%)",
    tabShopSelected:
      "linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(20,184,166,0.10) 55%, rgba(255,255,255,0.03) 100%)",
  },

  muted: "#CBD5E1",
};

const lightTheme: AppTheme = {
  ...lightThemeBase,
  teal: tealScale,
  accents: accentPalette,
  buttons: makeButtons("light", lightThemeBase),
};

const darkTheme: AppTheme = {
  ...darkThemeBase,
  teal: tealScale,
  accents: accentPalette,
  buttons: makeButtons("dark", darkThemeBase),
};

const themes: Record<ThemeName, AppTheme> = {
  light: lightTheme,
  dark: darkTheme,
};

const CustomThemeContext = createContext<CustomThemeContextValue | null>(null);

export const CustomThemeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();

  const [themeName, setThemeNameState] = useState<ThemeName>(() => {
    if (typeof window === "undefined") return "light";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === "dark" ? "dark" : "light";
  });

  const [isThemeReady, setIsThemeReady] = useState(false);

  const theme = themes[themeName];

  const persistTheme = useCallback(
    async (next: ThemeName) => {
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(STORAGE_KEY, next);
        }
      } catch (e) {
        console.warn("Theme localStorage save failed:", e);
      }

      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          await updateDoc(userRef, { theme: next });
        } catch (e) {
          console.error("Theme Firestore update failed:", e);
        }
      }
    },
    [user]
  );

  const setThemeByName = useCallback(
    async (next: ThemeName) => {
      setThemeNameState(next);
      await persistTheme(next);
    },
    [persistTheme]
  );

  const toggleTheme = useCallback(async () => {
    const next: ThemeName = themeName === "light" ? "dark" : "light";
    setThemeNameState(next);
    await persistTheme(next);
  }, [themeName, persistTheme]);

  useEffect(() => {
    let active = true;

    const fetchTheme = async () => {
      try {
        if (!user) {
          if (active) setIsThemeReady(true);
          return;
        }

        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (!active) return;

        if (docSnap.exists()) {
          const savedTheme = String(docSnap.data().theme || "").toLowerCase();
          if (savedTheme === "light" || savedTheme === "dark") {
            setThemeNameState(savedTheme as ThemeName);

            try {
              if (typeof window !== "undefined") {
                window.localStorage.setItem(STORAGE_KEY, savedTheme);
              }
            } catch (e) {
              console.warn("Theme localStorage sync failed:", e);
            }
          }
        }
      } catch (e) {
        console.error("Failed to fetch theme:", e);
      } finally {
        if (active) setIsThemeReady(true);
      }
    };

    fetchTheme();

    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;

    root.style.setProperty("--app-bg", theme.background);
    root.style.setProperty("--app-surface", theme.surface);
    root.style.setProperty("--app-surface-alt", theme.surfaceAlt);
    root.style.setProperty("--app-text", theme.text);
    root.style.setProperty("--app-text-muted", theme.textMuted);
    root.style.setProperty("--app-border", theme.border);
    root.style.setProperty("--app-primary", theme.primary);

    root.style.setProperty("--chakra-colors-teal-50", theme.teal[50] || "");
    root.style.setProperty("--chakra-colors-teal-100", theme.teal[100] || "");
    root.style.setProperty("--chakra-colors-teal-200", theme.teal[200]);
    root.style.setProperty("--chakra-colors-teal-300", theme.teal[300]);
    root.style.setProperty("--chakra-colors-teal-400", theme.teal[400]);
    root.style.setProperty("--chakra-colors-teal-500", theme.teal[500]);
    root.style.setProperty("--chakra-colors-teal-600", theme.teal[600]);
    root.style.setProperty("--chakra-colors-teal-700", theme.teal[700]);
    root.style.setProperty("--chakra-colors-teal-800", theme.teal[800] || "");
    root.style.setProperty("--chakra-colors-teal-900", theme.teal[900] || "");

    root.style.setProperty("color-scheme", themeName);
  }, [theme, themeName]);

  const value = useMemo<CustomThemeContextValue>(
    () => ({
      theme,
      themeName,
      isThemeReady,
      toggleTheme,
      setThemeByName,
    }),
    [theme, themeName, isThemeReady, toggleTheme, setThemeByName]
  );

  return (
    <CustomThemeContext.Provider value={value}>
      {children}
    </CustomThemeContext.Provider>
  );
};

export const useCustomTheme = () => {
  const context = useContext(CustomThemeContext);
  if (!context) {
    throw new Error("useCustomTheme must be used within CustomThemeProvider");
  }
  return context;
};